/**
 * Validation for the SOP "policy" reference link on the generate screen.
 *
 * The policy portal publishes documents at a fixed URL template:
 *   https://policy.a2z.com/docs/<numericId>/publication
 * A valid link must match this shape exactly: the `docs` segment, a numeric
 * document id, then the `publication` segment. A link that matches is a
 * well-formed, openable published-policy reference.
 */

/** Required scheme — the link is rendered into an anchor href, so only https. */
const REQUIRED_PROTOCOL = 'https:';

/** The policy portal host the document id lives under. */
const POLICY_HOST = 'policy.a2z.com';

/** Leading path segment before the numeric document id. */
const DOCS_SEGMENT = 'docs';

/** Trailing path segment that marks the published policy document. */
const PUBLICATION_SEGMENT = 'publication';

/** A policy document id: digits only. Shared by the validator and the URL
 *  builder so "what counts as an id" can't drift between them. Rejects empty,
 *  non-numeric, and mixed values (and so anything that could reshape the URL
 *  path, like `/`, `..`, `?`, or `#`). */
const POLICY_ID_PATTERN = /^\d+$/;

/**
 * True when `raw` is a published policy document link:
 * `https://policy.a2z.com/docs/<id>/publication` where `<id>` is one or more
 * digits. The `/publication` segment is REQUIRED. A trailing slash is
 * tolerated; a query string / hash is ignored. Parsed with `new URL()` so a
 * non-URL, a wrong scheme (`javascript:`, `http:`), or a different host is
 * rejected rather than matched by a loose string check.
 */
export function isValidPolicyUrl(raw: string): boolean {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return false;
  }
  if (url.protocol !== REQUIRED_PROTOCOL) return false;
  if (url.hostname !== POLICY_HOST) return false;
  // Path segments, dropping the empty strings from the leading slash and any
  // trailing slash: must be exactly ['docs', '<id>', 'publication'].
  const segments = url.pathname.split('/').filter((s) => s.length > 0);
  if (segments.length !== 3) return false;
  const [docs, id, publication] = segments;
  if (docs !== DOCS_SEGMENT) return false;
  if (publication !== PUBLICATION_SEGMENT) return false;
  // Digits-only id is required.
  return id !== undefined && POLICY_ID_PATTERN.test(id);
}

/**
 * Extracts the numeric policy/SOP id from a policy document link
 * (`https://policy.a2z.com/docs/<id>/publication`), or `undefined` when `raw`
 * isn't a valid policy URL. The id is the SOP number the generate agent accepts
 * as `agentInputs.sop_number` to scope which SOP it drafts from.
 *
 * Reuses {@link isValidPolicyUrl} so the accepted shape can't drift between
 * "is this link valid" and "what number does it carry".
 *
 * NOTE (future reference types): today the generate screen only offers a Link
 * reference, so the SOP number is parsed out of that link here. When the SOP
 * section gains Search and Upload (WHIZ-001 AC5), the caller must branch on the
 * reference TYPE, not funnel everything through this helper:
 *   - Link   → extract the number from the policy URL (this function).
 *   - Search → the chosen policy's id is already a number; pass it directly.
 *   - Upload → an uploaded document has NO policy number; do NOT send
 *     `sop_number` (the agent works from the uploaded content instead).
 */
export function extractSopNumber(raw: string): string | undefined {
  if (!isValidPolicyUrl(raw)) return undefined;
  // Safe to parse again — isValidPolicyUrl already proved it's a well-formed
  // policy URL, so the middle segment is the digits-only id.
  const segments = new URL(raw).pathname.split('/').filter((s) => s.length > 0);
  return segments[1];
}

/**
 * Builds the published policy document URL for a numeric SOP/policy id —
 * `https://policy.a2z.com/docs/<id>/publication`. The inverse of
 * {@link extractSopNumber}: the agent returns only the policy id
 * (`source_policy_id`), and this rebuilds the canonical link for the
 * question's source-SOP doc link. Shares the host/segment template with the
 * validator above so the two can't drift.
 *
 * Returns `undefined` unless the (trimmed) id is digits-only — blank, and also
 * any non-numeric or malformed value. The id comes from the agent
 * (`source_policy_id`), which is untrusted input; the resulting link is shown
 * as a locked, non-removable source-SOP row, so building a URL from garbage
 * would leave the reviewer with a broken reference they can't delete.
 */
export function buildPolicyUrl(sourcePolicyId: string): string | undefined {
  const id = sourcePolicyId.trim();
  if (!POLICY_ID_PATTERN.test(id)) return undefined;
  return `${REQUIRED_PROTOCOL}//${POLICY_HOST}/${DOCS_SEGMENT}/${id}/${PUBLICATION_SEGMENT}`;
}
